import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, UserPlus, MessageCircle, Check, Bell, Flame, Newspaper, Landmark, Shield, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import Avatar from "@/components/Avatar";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const KIND_COLOR = { lottery: "#FFD700", work: "#16A34A", flex: "#FF0055", housing: "#00E5FF", chaos: "#A78BFA", activity: "#A1A1AA" };

export default function Social() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("feed");
  const [feed, setFeed] = useState([]);
  const [players, setPlayers] = useState([]);
  const [convos, setConvos] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [news, setNews] = useState([]);
  const [gov, setGov] = useState(null);
  const [policeBusy, setPoliceBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, p, c, r, n, nw, gv] = await Promise.all([
        api.get("/social/feed"),
        api.get("/social/players"),
        api.get("/social/conversations"),
        api.get("/social/requests"),
        api.get("/social/notifications"),
        api.get("/phone/news"),
        api.get("/phone/government"),
      ]);
      setFeed(f.data.feed);
      setPlayers(p.data.players);
      setConvos(c.data.conversations);
      setReqs(r.data.requests);
      setNotifs(n.data.notifications);
      setNews(nw.data.news);
      setGov(gv.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reportCrime = async () => {
    setPoliceBusy(true);
    try {
      const { data } = await api.post("/phone/police/report", { kind: "noise" });
      setCharacter(data.character);
      toast.success("Police Station", { description: data.message });
    } catch (e) { toast.error(errMsg(e)); } finally { setPoliceBusy(false); }
  };


  const addFriend = async (id) => {
    try { await api.post("/social/friend-request", { target_id: id }); toast.success("Request sent 🤝"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const accept = async (reqId) => {
    try { await api.post("/social/friend-accept", { target_id: reqId }); toast.success("Padi added 🎉"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const flirt = async (id) => {
    try { const { data } = await api.post("/social/flirt", { target_id: id }); setCharacter(data.character); toast.success("Shot taken 💘"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const TABS = [
    { id: "feed", label: "Feed" },
    { id: "discover", label: "Discover" },
    { id: "dms", label: "DMs" },
    { id: "alerts", label: "Alerts" },
    { id: "news", label: "News" },
    { id: "gov", label: "Gov" },
    { id: "police", label: "Police" },
  ];

  return (
    <div className="px-5 pt-6" data-testid="social-page">
      <h1 className="font-display text-3xl">The <span className="text-[#FF0055]">Vibes</span></h1>

      <div className="mt-4 flex gap-1.5 bg-white border border-slate-200 rounded-full p-1 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`social-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 px-4 py-2 rounded-full label-xs transition-colors relative ${tab === t.id ? "bg-[#FF0055] text-white" : "text-slate-500"}`}>
            {t.label}
            {t.id === "alerts" && reqs.length > 0 && (
              <span className="absolute -top-1 -right-0 bg-[#FFD700] text-slate-900 text-[0.55rem] font-bold rounded-full w-4 h-4 flex items-center justify-center">{reqs.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-2.5 mb-4">
        {tab === "feed" && feed.map((f, i) => (
          <motion.div key={f.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
            data-testid="feed-item" className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-3">
            <Avatar src={f.avatar} size={40} idle />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-slate-900">{f.name}</span>
              <span className="text-slate-500"> {f.text}</span>
              <div className="label-xs mt-1" style={{ color: KIND_COLOR[f.kind] || "#A1A1AA" }}>{f.kind} · {timeAgo(f.created_at)}</div>
            </div>
          </motion.div>
        ))}
        {tab === "feed" && feed.length === 0 && <Empty text="Feed quiet. Go do something worth gist about." />}

        {tab === "discover" && players.map((p) => (
          <div key={p.id} data-testid={`player-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
            <button onClick={() => nav(`/player/${p.id}`)}><Avatar src={p.avatar} size={40} idle /></button>
            <button onClick={() => nav(`/player/${p.id}`)} className="flex-1 min-w-0 text-left">
              <div className="font-semibold text-slate-900 truncate">{p.name}</div>
              <div className="label-xs text-slate-500">{p.city} · Lv{p.level} · <span className="text-[#FF0055]">{p.clout} clout</span></div>
            </button>
            <button data-testid={`flirt-${p.id}`} onClick={() => flirt(p.id)} className="p-2 rounded-lg bg-[#FF0055]/15 text-[#FF0055] hover:bg-[#FF0055]/25 transition-colors"><Heart size={16} /></button>
            {p.is_friend ? (
              <button onClick={() => nav(`/chat/${p.id}`)} className="p-2 rounded-lg bg-[#16A34A]/15 text-[#16A34A]"><MessageCircle size={16} /></button>
            ) : p.requested ? (
              <span className="p-2 text-slate-400"><Check size={16} /></span>
            ) : (
              <button data-testid={`add-${p.id}`} onClick={() => addFriend(p.id)} className="p-2 rounded-lg bg-[#00E5FF]/15 text-[#00E5FF] hover:bg-[#00E5FF]/25 transition-colors"><UserPlus size={16} /></button>
            )}
          </div>
        ))}
        {tab === "discover" && players.length === 0 && <Empty text="No other players yet. Invite your padi dem." />}

        {tab === "dms" && convos.map((cv) => (
          <button key={cv.other_id} data-testid={`convo-${cv.other_id}`} onClick={() => nav(`/chat/${cv.other_id}`)}
            className="w-full bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 text-left hover:border-[#16A34A] transition-colors">
            <Avatar src={cv.avatar} size={40} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">{cv.name}</div>
              <div className="text-sm text-slate-500 truncate">{cv.last_text}</div>
            </div>
            <span className="label-xs text-slate-400">{timeAgo(cv.created_at)}</span>
          </button>
        ))}
        {tab === "dms" && convos.length === 0 && <Empty text="No DMs yet. Go slide into somebody's inbox." />}

        {tab === "alerts" && (
          <>
            {reqs.map((r) => (
              <div key={r.id} data-testid={`req-${r.id}`} className="bg-white border border-[#FFD700]/30 rounded-xl p-3 flex items-center gap-3">
                <Avatar src={r.from_avatar} size={40} />
                <div className="flex-1"><span className="font-semibold">{r.from_name}</span><span className="text-slate-500"> wan be your padi</span></div>
                <button data-testid={`accept-${r.id}`} onClick={() => accept(r.id)} className="label-xs bg-[#16A34A] text-white px-4 py-2 rounded-lg">Accept</button>
              </div>
            ))}
            {notifs.map((n) => (
              <div key={n.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                <Bell size={16} className="text-[#00E5FF] shrink-0" />
                <div className="flex-1 text-sm text-slate-600">{n.text}</div>
                <span className="label-xs text-slate-400">{timeAgo(n.created_at)}</span>
              </div>
            ))}
            {reqs.length === 0 && notifs.length === 0 && <Empty text="No alerts. Peace and quiet... for now." />}
          </>
        )}

        {tab === "news" && (
          <>
            {news.map((item, i) => (
              <motion.div key={`${item.tag}-${item.text.slice(0, 20)}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                data-testid="news-item" className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-3">
                <Newspaper size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="label-xs text-[#00E5FF]">{item.tag}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{item.text}</p>
                </div>
              </motion.div>
            ))}
            {news.length === 0 && <Empty text="No gist for now." />}
          </>
        )}

        {tab === "gov" && gov && (
          <div data-testid="gov-panel" className="space-y-3">
            <div className="bg-white border border-[#FFD700]/30 rounded-xl p-4">
              <div className="flex items-center gap-2 label-xs text-[#FFD700]"><Crown size={14} /> People's Champion</div>
              {gov.champion ? (
                <div className="mt-3 flex items-center gap-3">
                  <Avatar src={gov.champion.avatar} size={48} ring="#FFD700" />
                  <div>
                    <div className="font-display text-lg">{gov.champion.name}</div>
                    <div className="label-xs text-slate-500">{gov.champion.city} · <span className="text-[#FF0055]">{gov.champion.clout} clout</span></div>
                  </div>
                </div>
              ) : <p className="mt-2 text-sm text-slate-400">No champion yet. Fit be you.</p>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
              <Landmark size={18} className="text-[#16A34A] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-500">{gov.note}</p>
            </div>
          </div>
        )}

        {tab === "police" && (
          <div data-testid="police-panel" className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2"><Shield size={18} className="text-[#00E5FF]" /><span className="font-semibold">Report Wahala</span></div>
              <p className="text-sm text-slate-500 mt-2">See something off for your area? Report am. (Flavor only — small fun boost.)</p>
              <button data-testid="police-report-btn" disabled={policeBusy} onClick={reportCrime}
                className="mt-3 w-full py-3 rounded-xl font-bold label-xs bg-[#00E5FF] text-slate-900 disabled:opacity-50">File a Report</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Flame className="text-slate-300 mb-3" size={28} />
      <p className="text-sm text-slate-400 px-8">{text}</p>
    </div>
  );
}
